import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { studentAttendanceAPI } from '../../services/api';

const PublicAttendance = () => {
  const { qrCodeId } = useParams();
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('phone'); // phone, check-in, check-out, success
  const [learningSummary, setLearningSummary] = useState('');
  const [error, setError] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  // Validate QR code
  const { data: qrValidation, isLoading: isValidatingQR, error: qrError } = useQuery({
    queryKey: ['validate-qr', qrCodeId],
    queryFn: () => studentAttendanceAPI.validateQR(qrCodeId).then(res => res.data),
    retry: 1,
  });

  // Check attendance status
  const checkStatus = async (phoneNumber) => {
    setIsCheckingPhone(true);
    try {
      const response = await studentAttendanceAPI.getStatus(qrCodeId, phoneNumber);
      setAttendanceStatus(response.data);

      // Check if employee exists
      if (response.data.employee_found === false) {
        setError('Phone number not registered. Please contact your administrator.');
        setIsCheckingPhone(false);
        return;
      }

      if (response.data.is_checked_in) {
        if (response.data.check_out_time) {
          setStep('already-done');
        } else {
          setStep('check-out');
        }
      } else {
        setStep('check-in');
      }
      setIsCheckingPhone(false);
    } catch (err) {
      setError('Failed to verify phone number. Please try again.');
      setIsCheckingPhone(false);
    }
  };

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: (data) => studentAttendanceAPI.checkIn(qrCodeId, data),
    onSuccess: (response) => {
      setAttendanceStatus({
        is_checked_in: true,
        check_in_time: response.data.check_in_time,
        employee_id: response.data.employee_id,
      });
      setStep('check-in-success');
      setError('');
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Failed to check in');
    },
  });

  // Check-out mutation
  const checkOutMutation = useMutation({
    mutationFn: (data) => studentAttendanceAPI.checkOut(qrCodeId, data),
    onSuccess: (response) => {
      setStep('check-out-success');
      setError('');
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Failed to check out');
    },
  });

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    setError(''); // Clear any previous errors
    if (phone.length !== 10) {
      setError('Phone number must be 10 digits');
      return;
    }
    checkStatus(phone);
  };

  const requestLocationPermission = () => {
    setIsRequestingLocation(true);
    setError('');

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setIsRequestingLocation(false);
          setShowLocationModal(false);
          setError('');
        },
        (error) => {
          setIsRequestingLocation(false);
          let errorMessage = '';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please allow location access in your browser settings to mark attendance.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your device GPS settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'Unable to get your location. Please enable location services.';
          }
          setError(errorMessage);
          console.error('Geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setIsRequestingLocation(false);
      setError('Geolocation is not supported by your browser. Please use a modern browser.');
    }
  };

  const handleCheckIn = () => {
    // Check if we already have location
    if (userLocation) {
      checkInMutation.mutate({
        phone,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude
      });
    } else {
      // Show modal to request location
      setShowLocationModal(true);
    }
  };

  const proceedWithCheckIn = () => {
    if (userLocation) {
      checkInMutation.mutate({
        phone,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude
      });
    }
  };

  const handleCheckOut = (e) => {
    e.preventDefault();
    if (!learningSummary.trim()) {
      setError('Please describe what you learned today');
      return;
    }
    if (learningSummary.length < 10) {
      setError('Learning summary must be at least 10 characters');
      return;
    }

    // Check if we already have location
    if (userLocation) {
      checkOutMutation.mutate({
        phone,
        learning_summary: learningSummary,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude
      });
    } else {
      // Show modal to request location
      setShowLocationModal(true);
    }
  };

  const proceedWithCheckOut = () => {
    if (userLocation) {
      checkOutMutation.mutate({
        phone,
        learning_summary: learningSummary,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude
      });
    }
  };

  const formatTime = (dateString) => {
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  if (isValidatingQR) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (qrError || !qrValidation?.valid) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid QR Code</h1>
          <p className="text-gray-400">This QR code is invalid or has been deactivated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <ClockIcon className="h-16 w-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Student Attendance</h1>
          <p className="text-gray-400">Mark your daily attendance</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-700 rounded-lg p-4">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Phone Number Input */}
        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter your 10-digit phone number"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength="10"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isCheckingPhone}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckingPhone ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        )}

        {/* Check In */}
        {step === 'check-in' && (
          <div className="space-y-6">
            {attendanceStatus && (
              <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 mb-6">
                <p className="text-blue-300 text-sm">
                  Welcome, {attendanceStatus.student_name || 'Student'}
                </p>
              </div>
            )}
            <button
              onClick={handleCheckIn}
              disabled={checkInMutation.isLoading}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-4 px-4 rounded-lg transition-colors disabled:opacity-50 text-lg"
            >
              {checkInMutation.isLoading ? 'Checking In...' : 'Check In'}
            </button>
            <button
              onClick={() => {
                setPhone('');
                setStep('phone');
                setError('');
              }}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Use Different Number
            </button>
          </div>
        )}

        {/* Check In Success */}
        {step === 'check-in-success' && (
          <div className="text-center">
            <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Checked In Successfully!</h2>
            <p className="text-gray-400 mb-2">Time: {formatTime(attendanceStatus.check_in_time)}</p>
            <p className="text-gray-500 text-sm mb-6">Don't forget to check out at the end of the day</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        )}

        {/* Check Out */}
        {step === 'check-out' && (
          <div className="space-y-6">
            <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
              <p className="text-green-300 text-sm mb-1">Checked in at: {formatTime(attendanceStatus.check_in_time)}</p>
              <p className="text-gray-400 text-xs">Welcome back, {attendanceStatus.student_name}</p>
            </div>

            <form onSubmit={handleCheckOut} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  What did you learn today? *
                </label>
                <textarea
                  value={learningSummary}
                  onChange={(e) => setLearningSummary(e.target.value)}
                  placeholder="Describe what you learned today (minimum 10 characters)"
                  rows="6"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={checkOutMutation.isLoading}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-4 px-4 rounded-lg transition-colors disabled:opacity-50 text-lg"
              >
                {checkOutMutation.isLoading ? 'Checking Out...' : 'Check Out'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setPhone('');
                  setStep('phone');
                  setError('');
                }}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Check Out Success */}
        {step === 'check-out-success' && (
          <div className="text-center">
            <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Checked Out Successfully!</h2>
            <p className="text-gray-400 mb-6">Have a great day!</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        )}

        {/* Already Done */}
        {step === 'already-done' && (
          <div className="text-center">
            <CheckCircleIcon className="h-20 w-20 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Already Marked!</h2>
            <div className="bg-gray-700 rounded-lg p-4 mb-6 text-left">
              <p className="text-gray-300 text-sm mb-1">Check-in: {formatTime(attendanceStatus.check_in_time)}</p>
              <p className="text-gray-300 text-sm">Check-out: {formatTime(attendanceStatus.check_out_time)}</p>
            </div>
            <p className="text-gray-400 mb-6">You've already completed attendance for today</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-700">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-900/30 mb-4">
                <svg className="h-10 w-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Location Permission Required</h3>
              <p className="text-gray-300 text-sm">
                To mark attendance, we need to verify that you're at the correct location. Please allow access to your location when prompted by your browser.
              </p>
            </div>

            {userLocation ? (
              <div className="mb-4 bg-green-900/20 border border-green-700 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                  <p className="text-green-300 text-sm">Location access granted!</p>
                </div>
              </div>
            ) : null}

            <div className="space-y-3">
              <button
                onClick={requestLocationPermission}
                disabled={isRequestingLocation || userLocation}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isRequestingLocation ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Requesting Permission...
                  </>
                ) : userLocation ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Location Granted
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Allow Location Access
                  </>
                )}
              </button>

              {userLocation && (
                <button
                  onClick={() => {
                    setShowLocationModal(false);
                    if (step === 'check-in') {
                      proceedWithCheckIn();
                    } else if (step === 'check-out') {
                      proceedWithCheckOut();
                    }
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Continue
                </button>
              )}

              <button
                onClick={() => setShowLocationModal(false)}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-400">
                <strong className="text-gray-300">Privacy Note:</strong> Your location is only used to verify attendance and is not stored or shared for any other purpose.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicAttendance;
